/**
 * RecipeHeaderStrip — Recipe selection header strip component.
 *
 * Shown above the graph area and side panel. Displays recipe title,
 * condensed description, an optional "Per a" yield row, and an actions
 * group:
 *   - Focus toggle: always shown with a recipe selected (either mode) —
 *     folds/unfolds its main root, which also unfocuses/focuses it (the
 *     one place other recipes get hidden so this one is the only thing
 *     shown). A toggle, not an arm/confirm: filled when focused/unfolded.
 *   - Normal mode: [reload (only with a recipe selected)]
 *   - Edit mode:   [save changes] [discard changes]
 * Reload and save/discard are mutually exclusive by mode. The edit-mode
 * toggle itself now lives in the site nav (SiteNav.ts), not here — see
 * setEditModeActive()'s doc comment.
 *
 * See docs/design/editing-capabilities.md §7.6 for design specification.
 */
import type { Tag } from '../api/types.js';
import { tagChipStyle } from '../tags/tagColors.js';
import { tagGraphCache } from '../tags/tagGraph.js';

// No plain (non-emoji) magnifying glass exists in Unicode — using a
// crosshair/target glyph instead, which reads reasonably as "focus".
const ICON_FOCUS = '⌖';
const ICON_REFRESH = '↻';
const ICON_SAVE = '✓';
const ICON_DISCARD = '✕';

export class RecipeHeaderStrip {
  private element: HTMLElement | null = null;
  private toolbarSlot: HTMLElement | null = null;
  private titleEl: HTMLElement | null = null;
  private descEl: HTMLElement | null = null;
  private descTextEl: HTMLElement | null = null;
  private tagsRow: HTMLElement | null = null;
  private tagsListEl: HTMLElement | null = null;
  private quantityRow: HTMLElement | null = null;
  private quantityClickable: HTMLElement | null = null;
  private quantityOnChange: ((amount: number, unit: string) => void) | null = null;
  private quantityOnClick: (() => void) | null = null;
  private quantityVisible: boolean = false;
  private focusBtn: HTMLButtonElement | null = null;
  private refreshBtn: HTMLButtonElement | null = null;
  private saveBtn: HTMLButtonElement | null = null;
  private discardBtn: HTMLButtonElement | null = null;
  private actionsGroup: HTMLElement | null = null;

  /**
   * Render the recipe header strip into the given container.
   * The strip is always visible. When no recipe is selected it shows
   * a placeholder:  >  -  (icon + dash).
   */
  render(
    container: HTMLElement,
    onDeselect: () => void,
    onRefresh?: () => void,
    onSave?: () => void,
    onDiscard?: () => void,
    onToggleFocus?: () => void,
  ): void {
    // Remove existing strip if present
    const existing = container.querySelector('.recipe-header-strip');
    if (existing) existing.remove();

    this.element = document.createElement('div');
    this.element.className = 'recipe-header-strip';
    this.element.style.cssText = `
      display: flex;
      flex-direction: column;
      background: var(--accent-alpha-10);
      border: 1px solid var(--accent-alpha-20);
      border-radius: 8px;
      padding: 8px 12px;
      margin-bottom: 8px;
      font-family: 'Courier New', Consolas, 'Liberation Mono', monospace;
    `;

    // Top row: title/description/quantity + actions — the strip's original
    // content. The edit toolbar (below) is a second row inside the same
    // bordered box, separated by a thin rule when it's visible.
    const topRow = document.createElement('div');
    topRow.className = 'recipe-header-top-row';
    topRow.style.cssText = `
      display: flex;
      gap: 8px;
      flex-wrap: nowrap;
      align-items: flex-start;
    `;

    // Left: > icon + title
    const titleGroup = document.createElement('div');
    titleGroup.style.cssText = 'display: flex; flex-direction: column; flex: 1; min-width: 0;';

    const titleRow = document.createElement('div');
    titleRow.style.cssText = 'display: flex; align-items: flex-start; gap: 6px; min-width: 0;';

    const icon = document.createElement('span');
    icon.textContent = '>';
    icon.style.cssText = 'color: var(--accent); font-size: 16px; flex-shrink: 0; line-height: 1.3;';
    titleRow.appendChild(icon);

    // Title always wraps (rather than truncating) so it never pushes the
    // actions group onto a second row — only the title grows taller.
    this.titleEl = document.createElement('span');
    this.titleEl.className = 'recipe-header-title';
    this.titleEl.style.cssText = 'color: #e0e0e0; font-weight: bold; font-size: 15px; line-height: 1.3; white-space: normal; overflow-wrap: break-word; flex-shrink: 1; min-width: 0;';
    this.titleEl.textContent = '—';
    titleRow.appendChild(this.titleEl);

    titleGroup.appendChild(titleRow);

    // ── Quantity row: "Per a <amount> <unit>" (hidden by default) ──
    // Clicking opens a modal for editing (only in non-edit mode).
    // In edit mode the quantity is shown as static text (not clickable).
    this.quantityRow = document.createElement('div');
    this.quantityRow.className = 'recipe-header-quantity';
    this.quantityRow.style.cssText = 'margin-left: 0; display: none; align-items: center; gap: 4px; min-height: 0;';

    // Inline spacer matching the icon+gap offset so text aligns with the title
    const qtySpacer = document.createElement('span');
    qtySpacer.style.cssText = 'display: inline-block; width: 11px; flex-shrink: 0;';
    this.quantityRow.appendChild(qtySpacer);

    this.quantityClickable = document.createElement('span');
    this.quantityClickable.style.cssText = 'color: #a0a0a0; font-size: 12px; white-space: nowrap; cursor: pointer; transition: color 0.15s; border-bottom: 1px dashed var(--accent-alpha-27);';
    this.quantityClickable.addEventListener('mouseenter', () => {
      if (this.quantityClickable) {
        this.quantityClickable.style.color = 'var(--accent)';
        this.quantityClickable.style.borderBottomColor = 'var(--accent-light)';
      }
    });
    this.quantityClickable.addEventListener('mouseleave', () => {
      if (this.quantityClickable) {
        this.quantityClickable.style.color = '#a0a0a0';
        this.quantityClickable.style.borderBottomColor = 'var(--accent-alpha-27)';
      }
    });
    this.quantityRow.appendChild(this.quantityClickable);

    titleGroup.appendChild(this.quantityRow);

    // ── Description row (hidden by default) ──
    this.descEl = document.createElement('div');
    this.descEl.className = 'recipe-header-desc';
    this.descEl.style.cssText = 'display: none; align-items: center; min-height: 0;';

    // Spacer to align with title text (after the > icon)
    const descSpacer = document.createElement('span');
    descSpacer.style.cssText = 'display: inline-block; width: 11px; flex-shrink: 0;';
    this.descEl.appendChild(descSpacer);

    this.descTextEl = document.createElement('div');
    this.descTextEl.style.cssText = 'color: #888; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0;';
    this.descEl.appendChild(this.descTextEl);

    titleGroup.appendChild(this.descEl);

    // ── Tags row (hidden by default) — the main root's own ingredient's
    // tags, if it has one (most main roots don't, so this row usually
    // stays hidden). ──
    this.tagsRow = document.createElement('div');
    this.tagsRow.className = 'recipe-header-tags';
    this.tagsRow.style.cssText = 'display: none; align-items: flex-start; gap: 6px; min-height: 0; margin-top: 4px;';

    const tagsSpacer = document.createElement('span');
    tagsSpacer.style.cssText = 'display: inline-block; width: 11px; flex-shrink: 0;';
    this.tagsRow.appendChild(tagsSpacer);

    this.tagsListEl = document.createElement('div');
    this.tagsListEl.style.cssText = 'display: flex; flex-wrap: wrap; gap: 4px; flex: 1; min-width: 0;';
    this.tagsRow.appendChild(this.tagsListEl);

    titleGroup.appendChild(this.tagsRow);

    topRow.appendChild(titleGroup);

    // Actions: reload OR save/discard (mutually exclusive by mode), plus
    // the edit-mode toggle, which is always visible. Always stay on the
    // same row as the title — the title wraps instead, so this group
    // never gets pushed onto its own line.
    this.actionsGroup = document.createElement('div');
    this.actionsGroup.className = 'recipe-header-actions';
    this.actionsGroup.style.cssText = 'display: flex; align-items: center; gap: 6px; flex-shrink: 0;';

    // Focus toggle — left of reload. A plain toggle (not arm/confirm):
    // filled when the recipe is focused (its main root is unfolded).
    this.focusBtn = this.makeIconButton(ICON_FOCUS, 'Centra la recepta');
    this.focusBtn.style.display = 'none';
    if (onToggleFocus) {
      this.focusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        onToggleFocus();
      });
    }
    this.actionsGroup.appendChild(this.focusBtn);

    this.refreshBtn = this.makeIconButton(ICON_REFRESH, 'Recarrega la recepta');
    this.refreshBtn.style.display = 'none';
    if (onRefresh) {
      this.refreshBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        onRefresh();
      });
    }
    this.actionsGroup.appendChild(this.refreshBtn);

    this.saveBtn = this.makeIconButton(ICON_SAVE, 'Desa els canvis', true);
    this.saveBtn.style.display = 'none';
    if (onSave) {
      this.saveBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        onSave();
      });
    }
    this.actionsGroup.appendChild(this.saveBtn);

    this.discardBtn = this.makeIconButton(ICON_DISCARD, 'Descarta els canvis');
    this.discardBtn.style.display = 'none';
    if (onDiscard) {
      this.discardBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        onDiscard();
      });
    }
    this.actionsGroup.appendChild(this.discardBtn);

    topRow.appendChild(this.actionsGroup);
    this.element.appendChild(topRow);

    // Toolbar slot: mount point for the EditToolbar, appended below the
    // top row. Empty (and thus invisible, no separator) until something is
    // mounted into it.
    this.toolbarSlot = document.createElement('div');
    this.toolbarSlot.className = 'recipe-header-toolbar-slot';
    this.element.appendChild(this.toolbarSlot);

    container.prepend(this.element);
  }

  private makeIconButton(icon: string, title: string, accent: boolean = false): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.title = title;
    btn.textContent = icon;
    btn.style.cssText = `
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      font-weight: bold;
      font-family: inherit;
      border-radius: 50%;
      border: 1px solid var(--accent-alpha-40);
      background: ${accent ? 'var(--accent)' : 'transparent'};
      color: ${accent ? '#0d1117' : 'var(--accent)'};
      cursor: pointer;
      transition: all 0.15s ease;
      flex-shrink: 0;
    `;
    return btn;
  }

  /**
   * The slot below the title row where the EditToolbar mounts itself,
   * inside this same bordered header box.
   */
  getToolbarSlot(): HTMLElement | null {
    return this.toolbarSlot;
  }

  /**
   * Show the header strip.
   */
  show(): void {
    if (this.element) {
      this.element.style.display = 'flex';
    }
  }

  /**
   * Hide the header strip.
   */
  hide(): void {
    if (this.element) {
      this.element.style.display = 'none';
    }
  }

  /**
   * Switch to the empty/placeholder state — shown when no recipe is
   * selected. Displays  >  -   with no description. The edit-mode toggle
   * stays visible (it doesn't depend on a recipe being selected).
   */
  showEmpty(): void {
    if (this.titleEl) this.titleEl.textContent = '-';
    if (this.descEl) {
      this.descEl.style.display = 'none';
    }
    if (this.descTextEl) {
      this.descTextEl.textContent = '';
    }
    if (this.quantityRow) this.quantityRow.style.display = 'none';
    if (this.tagsRow) this.tagsRow.style.display = 'none';
    this.hideFocusButton();
    this.hideRefreshButton();
    if (this.actionsGroup) this.actionsGroup.style.display = 'flex';
    this.show();
  }

  /**
   * Show the focus toggle button — only meaningful with a recipe selected.
   */
  showFocusButton(): void {
    if (this.focusBtn) this.focusBtn.style.display = 'flex';
  }

  /**
   * Hide the focus toggle button.
   */
  hideFocusButton(): void {
    if (this.focusBtn) this.focusBtn.style.display = 'none';
  }

  /**
   * Reflect whether the recipe is currently focused (its main root
   * unfolded — filled) or not (folded — outline).
   */
  setFocusActive(active: boolean): void {
    if (!this.focusBtn) return;
    this.focusBtn.style.background = active ? 'var(--accent)' : 'transparent';
    this.focusBtn.style.color = active ? '#0d1117' : 'var(--accent)';
    this.focusBtn.style.borderColor = active ? 'var(--accent)' : 'var(--accent-alpha-40)';
    this.focusBtn.title = active ? 'Treu el focus de la recepta' : 'Centra la recepta';
  }

  /**
   * Reflect whether edit mode is on: switches which of reload vs.
   * save/discard applies — leaving edit mode hides save/discard (reload's
   * own visibility is up to the caller, e.g. shown again if a recipe is
   * selected); entering it hides reload (save/discard's visibility still
   * follows whether there are pending changes, via
   * showSaveButton/hideSaveButton). The toggle button itself lives in the
   * site nav now (SiteNav.ts), not here.
   */
  setEditModeActive(active: boolean): void {
    if (active) {
      this.hideRefreshButton();
    } else {
      this.hideSaveButton();
      this.hideDiscardButton();
    }
  }

  /**
   * Show the refresh button.
   */
  showRefreshButton(): void {
    if (this.refreshBtn) {
      this.refreshBtn.style.display = 'flex';
    }
  }

  /**
   * Hide the refresh button.
   */
  hideRefreshButton(): void {
    if (this.refreshBtn) {
      this.refreshBtn.style.display = 'none';
    }
  }

  /**
   * Show the save button.
   */
  showSaveButton(): void {
    if (this.saveBtn) {
      this.saveBtn.style.display = 'flex';
    }
  }

  /**
   * Hide the save button.
   */
  hideSaveButton(): void {
    if (this.saveBtn) {
      this.saveBtn.style.display = 'none';
    }
  }

  /**
   * Show the discard button.
   */
  showDiscardButton(): void {
    if (this.discardBtn) {
      this.discardBtn.style.display = 'flex';
    }
  }

  /**
   * Hide the discard button.
   */
  hideDiscardButton(): void {
    if (this.discardBtn) {
      this.discardBtn.style.display = 'none';
    }
  }

  /** Update the save button's tooltip text for feedback states (e.g. "Guardant…"). */
  setSaveButtonText(text: string): void {
    if (this.saveBtn) {
      this.saveBtn.title = text;
    }
  }

  /**
   * Enable or disable the save button.
   */
  setSaveButtonDisabled(disabled: boolean): void {
    if (this.saveBtn) {
      (this.saveBtn as HTMLButtonElement).disabled = disabled;
      this.saveBtn.style.opacity = disabled ? '0.6' : '1';
    }
  }

  /**
   * Update the title and description displayed in the strip.
   */
  update(title: string, description?: string): void {
    if (this.titleEl) {
      this.titleEl.textContent = title;
    }
    if (this.descEl) {
      if (description) {
        this.descTextEl!.textContent = `// ${description}`;
        this.descEl.style.display = 'flex';
      } else {
        if (this.descTextEl) this.descTextEl.textContent = '';
        this.descEl.style.display = 'none';
      }
    }
    if (this.actionsGroup) this.actionsGroup.style.display = 'flex';
  }

  /**
   * Update the tags row — the main root's own ingredient's tags, if it
   * has one (most main roots don't, since they're usually a plain
   * transformation/name node for the dish itself rather than an
   * ingredient — the row just stays hidden then). Called from
   * GraphVisualizer.updateRecipeHeader() right after update().
   */
  setTags(tags: Tag[]): void {
    if (!this.tagsRow || !this.tagsListEl) return;
    if (tags.length === 0) {
      this.tagsRow.style.display = 'none';
      return;
    }
    const tagsListEl = this.tagsListEl;
    tagsListEl.innerHTML = '';
    for (const tag of tags) {
      tagsListEl.appendChild(this.buildTagChip(tag, false));
    }
    this.tagsRow.style.display = 'flex';

    // Implied tags (via the tag-implication graph) render alongside the
    // explicit ones above, dimmed — patched in once the graph cache
    // resolves, guarded against a since-replaced row (a later setTags()
    // call, e.g. selecting a different node, or destroy()).
    tagGraphCache.ensureLoaded().then(() => {
      if (!tagsListEl.isConnected) return;
      for (const tag of tagGraphCache.getImpliedTags(tags)) {
        tagsListEl.appendChild(this.buildTagChip(tag, true));
      }
    }).catch((error) => {
      console.error('Failed to load tag implications:', error);
    });
  }

  private buildTagChip(tag: Tag, implied: boolean): HTMLElement {
    const chip = document.createElement('span');
    chip.style.cssText = `font-size: 11px; padding: 1px 8px; border-radius: 9999px; border-width: 1px; border-style: solid; font-family: inherit; ${tagChipStyle(tag.color)}${implied ? 'opacity: 0.55;' : ''}`;
    chip.textContent = tag.name;
    if (implied) chip.title = 'Implícita — no assignada directament';
    return chip;
  }

  /**
   * Update the quantity (yield) display in the header strip.
   *
   * @param qty - The quantity to display, or null to hide the row.
   * @param unitOptions - Array of {id, label} for unit lookups.
   * @param isEditMode - If true, quantity is static text (not clickable).
   * @param onClick - Callback fired when the quantity line is clicked (opens modal).
   * @param onChange - Callback fired from the modal when amount/unit changes.
   */
  updateQuantity(
    qty: { amount: number; unit: string } | null,
    unitOptions?: { id: string; label: string }[],
    isEditMode?: boolean,
    onClick?: () => void,
    onChange?: (amount: number, unit: string) => void,
  ): void {
    if (!this.quantityRow || !this.quantityClickable) return;

    this.quantityOnClick = onClick ?? null;
    this.quantityOnChange = onChange ?? null;
    this.quantityVisible = qty !== null;

    if (!qty) {
      this.quantityRow.style.display = 'none';
      return;
    }

    this.quantityRow.style.display = 'flex';

    // Build display text: "Per a <amount> <unit>"
    const unitDef = unitOptions?.find(o => o.id === qty.unit);
    const unitLabel = unitDef?.label ?? qty.unit;
    this.quantityClickable.textContent = `Per a ${this.formatNumber(qty.amount)} ${unitLabel}`;

    if (isEditMode) {
      // Edit mode: static text, no click handler, no underline
      this.quantityClickable.style.cursor = 'default';
      this.quantityClickable.style.borderBottom = 'none';
      this.quantityClickable.onclick = null;
    } else {
      // Normal mode: clickable to open modal, dashed underline
      this.quantityClickable.style.cursor = 'pointer';
      this.quantityClickable.style.borderBottom = '1px dashed var(--accent-alpha-27)';
      this.quantityClickable.onclick = () => {
        this.quantityOnClick?.();
      };
    }
  }

  private formatNumber(n: number): string {
    if (Number.isInteger(n)) return String(n);
    return n.toFixed(2).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  }

  /**
   * Remove the strip from the DOM.
   */
  destroy(): void {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this.toolbarSlot = null;
    this.titleEl = null;
    this.descEl = null;
    this.descTextEl = null;
    this.tagsRow = null;
    this.tagsListEl = null;
    this.quantityRow = null;
    this.quantityClickable = null;
    this.quantityOnChange = null;
    this.quantityOnClick = null;
    this.focusBtn = null;
    this.refreshBtn = null;
    this.saveBtn = null;
    this.discardBtn = null;
  }
}
